import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

// One piece of page state kept in the query string so a view can be shared or reloaded.
// Only non-default values are written, so existing bare URLs keep their current behaviour.
// The default's type picks the codec: string, number, boolean, or array of strings (CSV).
export function decodeParam(raw, defaultValue) {
  if (raw === null || raw === undefined || raw === '') return defaultValue;
  if (typeof defaultValue === 'number') {
    const n = Number(raw);
    return Number.isFinite(n) ? n : defaultValue;
  }
  if (typeof defaultValue === 'boolean') return raw === '1' || raw === 'true';
  if (Array.isArray(defaultValue)) return raw.split(',').filter(Boolean);
  return raw;
}

export function encodeParam(value, defaultValue) {
  if (Array.isArray(defaultValue)) {
    const v = Array.isArray(value) ? value : [];
    const same = v.length === defaultValue.length && v.every((x, i) => x === defaultValue[i]);
    return same || v.length === 0 ? null : v.join(',');
  }
  if (value === defaultValue || value === null || value === undefined || value === '') return null;
  if (typeof value === 'boolean') return value ? '1' : null;
  return String(value);
}

// Writes can outrun the render that applies them (two setters in one handler, a key press right
// after a click), and react-router's functional updates read render-time params. `lastWrite`
// remembers the newest string written and the rendered string it was built on: while the render
// is still the old one, the next write builds on the pending string instead of the stale params.
// Later writes in the same tick replace the history entry the first one pushed.
const lastWrite = { rendered: null, search: null };
const sameTick = { on: false };

// Applies `mutate(URLSearchParams)` on top of any write not yet rendered.
export function commitParams(searchParams, setSearchParams, mutate, replace) {
  const rendered = searchParams.toString();
  const base = new URLSearchParams(lastWrite.rendered === rendered && lastWrite.search !== null ? lastWrite.search : rendered);
  mutate(base);
  const chained = sameTick.on;
  sameTick.on = true;
  queueMicrotask(() => { sameTick.on = false; });
  lastWrite.rendered = rendered;
  lastWrite.search = base.toString();
  setSearchParams(base, { replace: replace || chained });
}

export default function useUrlState(key, defaultValue, { replace = false, allowed = null } = {}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const defaultRef = useRef(defaultValue);
  // Optimistic copy: the router applies a navigation a render later, and a controlled input
  // (checkbox, switch) must reflect the click in the same event. Dropped once the URL catches up.
  const [optimistic, setOptimistic] = useState(null);
  useEffect(() => { setOptimistic(null); lastWrite.search = null; }, [searchParams]);
  let value = optimistic ? optimistic.v : decodeParam(searchParams.get(key), defaultRef.current);
  if (allowed && !allowed.includes(value)) value = defaultRef.current;

  const set = useCallback((next) => {
    if (typeof next !== 'function') setOptimistic({ v: next });
    commitParams(searchParams, setSearchParams, (base) => {
      const resolved = typeof next === 'function' ? next(decodeParam(base.get(key), defaultRef.current)) : next;
      const encoded = encodeParam(resolved, defaultRef.current);
      if (encoded === null) base.delete(key); else base.set(key, encoded);
    }, replace);
  }, [key, replace, searchParams, setSearchParams]);

  return [value, set];
}
